import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Trash2, TrendingDown, Target } from 'lucide-react';
import { toast } from 'react-toastify';

const FRAMEWORKS = ['SBTi 1.5°C', 'SBTi Well-Below 2°C', 'Net Zero', 'Paris Aligned', 'Custom'];
const SCOPES = [
  { value: 'scope1', label: 'Scope 1 only' },
  { value: 'scope2', label: 'Scope 2 only' },
  { value: 'scope1_2', label: 'Scope 1 + 2' },
  { value: 'scope1_2_3', label: 'Scope 1 + 2 + 3' },
];

function GHGTargets() {
  const [targets, setTargets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    target_type: 'absolute', scope: 'scope1_2', base_year: 2024,
    base_year_emissions: '', target_year: 2030, reduction_pct: 50, framework: 'SBTi 1.5°C'
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        api.get('/ghg/targets'),
        api.get(`/ghg/summary?year=${new Date().getFullYear()}`),
      ]);
      setTargets(tRes.data.targets || []);
      setSummary(sRes.data);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await api.post('/ghg/targets', form);
      toast.success('Target set');
      setShowForm(false);
      loadAll();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    await api.delete(`/ghg/targets/${id}`);
    loadAll();
  };

  const targetEmissions = form.base_year_emissions ? (form.base_year_emissions * (1 - form.reduction_pct / 100)).toFixed(1) : '—';

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: '#0A1628', margin: '0 0 4px' }}>Reduction Targets</h1>
          <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Set and track science-based emissions reduction targets</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Set Target
        </button>
      </div>

      {/* Current emissions context */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
          {[
            { label: `Current Year CO2e (${new Date().getFullYear()})`, value: `${summary.total_co2e?.toLocaleString() || 0} mt`, color: '#0066FF', bg: '#EFF6FF' },
            { label: 'Scope 1 + 2', value: `${Math.round((parseFloat(summary.by_scope?.find(s=>s.scope===1)?.total_co2e||0) + parseFloat(summary.by_scope?.find(s=>s.scope===2)?.total_co2e||0))).toLocaleString()} mt`, color: '#16A34A', bg: '#F0FDF4' },
            { label: 'Active Targets', value: targets.filter(t => t.status === 'active').length, color: '#D97706', bg: '#FFFBEB' },
          ].map((s, i) => (
            <div key={i} className="card" style={{ padding: '14px 16px', background: s.bg, border: 'none' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: 'var(--font-heading)' }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add target form */}
      {showForm && (
        <div className="card" style={{ padding: '20px', marginBottom: 20, border: '2px solid #16A34A' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 16 }}>New Reduction Target</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            {[
              { label: 'Framework', key: 'framework', type: 'select', options: FRAMEWORKS.map(f => ({ value: f, label: f })) },
              { label: 'Scope Coverage', key: 'scope', type: 'select', options: SCOPES },
              { label: 'Target Type', key: 'target_type', type: 'select', options: [{ value: 'absolute', label: 'Absolute' }, { value: 'intensity', label: 'Intensity' }] },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>{field.label}</label>
                <select value={form[field.key]} onChange={e => setForm({...form, [field.key]: e.target.value})}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                  {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            {[
              { label: 'Base Year', key: 'base_year', type: 'number', placeholder: '2024' },
              { label: 'Base Year Emissions (mt CO2e)', key: 'base_year_emissions', type: 'number', placeholder: '10000' },
              { label: 'Target Year', key: 'target_year', type: 'number', placeholder: '2030' },
              { label: 'Reduction %', key: 'reduction_pct', type: 'number', placeholder: '50' },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>{field.label}</label>
                <input type={field.type} value={form[field.key]} onChange={e => setForm({...form, [field.key]: e.target.value})}
                  placeholder={field.placeholder}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
          {form.base_year_emissions && (
            <div style={{ padding: '12px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 13, color: '#15803D', fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                Target: reduce from {parseFloat(form.base_year_emissions).toLocaleString()} mt to {targetEmissions} mt CO2e by {form.target_year} ({form.reduction_pct}% reduction)
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowForm(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14 }}>Cancel</button>
            <button onClick={handleSubmit} className="btn btn-primary">Save Target</button>
          </div>
        </div>
      )}

      {/* Targets list */}
      {targets.length === 0 ? (
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <Target size={40} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', marginBottom: 8 }}>No targets set yet</div>
          <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'var(--font-body)', marginBottom: 20 }}>Set a science-based target to track your decarbonization progress</div>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">Set First Target</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {targets.map(t => {
            const currentCO2e = summary?.total_co2e || 0;
            const progressPct = t.base_year_emissions > 0
              ? Math.min(Math.round(((t.base_year_emissions - currentCO2e) / (t.base_year_emissions - t.target_emissions)) * 100), 100)
              : 0;
            const onTrack = progressPct >= Math.round(((new Date().getFullYear() - t.base_year) / (t.target_year - t.base_year)) * 100);
            return (
              <div key={t.id} className="card" style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628' }}>{t.framework}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: onTrack ? '#F0FDF4' : '#FEF2F2', color: onTrack ? '#16A34A' : '#DC2626' }}>
                        {onTrack ? 'On Track' : 'Behind'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'var(--font-body)' }}>
                      {t.reduction_pct}% reduction by {t.target_year} · {SCOPES.find(s=>s.value===t.scope)?.label} · Base year: {t.base_year}
                    </div>
                  </div>
                  <button onClick={() => handleDelete(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                  {[
                    { label: 'Base Year Emissions', value: `${parseFloat(t.base_year_emissions).toLocaleString()} mt` },
                    { label: 'Target Emissions', value: `${parseFloat(t.target_emissions).toLocaleString()} mt` },
                    { label: 'Current Emissions', value: `${parseFloat(currentCO2e).toLocaleString()} mt` },
                  ].map((s, i) => (
                    <div key={i} style={{ padding: '10px 14px', background: '#F8FAFC', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)', marginBottom: 2 }}>{s.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#0A1628', fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Progress toward target</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: onTrack ? '#16A34A' : '#DC2626', fontFamily: 'var(--font-body)' }}>{progressPct}%</span>
                  </div>
                  <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: 8, width: `${Math.max(progressPct, 0)}%`, background: onTrack ? '#16A34A' : '#D97706', borderRadius: 4, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GHGTargets;
